import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { sectionVariant, staggerContainer, itemVariant, viewportOnce } from "@/lib/motion";
import { usePageContent } from "@/hooks/usePageContent";

const fallback = {
  label: "About",
  headline: "A growth partner, not an agency.",
  intro: "XperfoMax was founded on a simple premise: ambitious brands deserve partners who understand growth deeply. Partners who don't need to shout about it. We combine the strategic thinking of a consultancy with the execution capability of a performance agency.",
  values: [
    { title: "Clarity over complexity", description: "We make the complicated simple. In strategy, in reporting, in communication." },
    { title: "Outcomes over outputs", description: "We measure success by business impact, not activity metrics." },
    { title: "Partnership over vendor", description: "We work with you, not just for you. Your success is our success." },
    { title: "Long-term over short-term", description: "We build systems that compound, not tactics that fade." },
  ],
  howWeWorkTitle: "How we work",
  howWeWork: [
    { bold: "Small teams, senior talent.", text: "You work directly with strategists and specialists. Not account managers who relay messages." },
    { bold: "Transparent by default.", text: "Real-time access to your data, honest conversations about what's working, and clear reporting." },
    { bold: "Aligned incentives.", text: "We're interested in long-term partnerships, not quick wins. Our success is tied to yours." },
  ],
  ctaText: "Start a conversation",
};

const About = () => {
  const { content } = usePageContent("about", fallback);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 md:pt-28 lg:pt-32 pb-12 md:pb-20 lg:pb-24">
        <div className="container-wide">
          {/* Header */}
          <motion.div 
            className="max-w-3xl mb-10 md:mb-16 lg:mb-20"
            variants={sectionVariant}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="accent-bar" />
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                {content.label}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-8">
              {content.headline}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {content.intro}
            </p>
          </motion.div>

          {/* Values */}
          <motion.div 
            className="grid md:grid-cols-2 gap-6 md:gap-10 lg:gap-12 mb-12 md:mb-20 lg:mb-24"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
          >
            {content.values.map((value: any, index: number) => (
              <motion.div 
                key={value.title} 
                className="border-t border-border pt-8"
                variants={itemVariant}
              >
                <span className="text-sm text-accent font-medium mb-3 block">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="text-lg font-medium mb-3">
                  {value.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Approach */}
          <motion.div 
            className="bg-card p-5 md:p-10 lg:p-16"
            variants={sectionVariant}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
          >
            <h2 className="text-2xl font-medium mb-6">
              {content.howWeWorkTitle}
            </h2>
            <div className="grid md:grid-cols-3 gap-8 text-muted-foreground leading-relaxed">
              {content.howWeWork.map((item: any, i: number) => (
                <p key={i}>
                  <strong className="text-foreground">{item.bold}</strong> {item.text}
                </p>
              ))}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div 
            className="mt-10 md:mt-16 lg:mt-20 text-center"
            variants={sectionVariant}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
          >
            <Button variant="primary" size="lg" asChild>
              <Link to="/contact">
                {content.ctaText}
              </Link>
            </Button>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default About;
